import { createStore } from "vuex";

import config from "./modules/config";
import includes from "./modules/includes";
import user from "./modules/user";
import platforms from "./modules/platforms";
import validation from "./modules/validation";

/**
 * The Vuex store configuration for the application.
 * It defines the various modules that make up the store, including:
 * - `user`: manages user-related data and actions
 * - `platforms`: manages platform-related data and actions
 * - `config`: manages application configuration data and actions
 * - `includes`: manages the loading of various include files
 * - `validation`: manages the validation of form steps
 *
 * The `initializeApp` action is responsible for initializing the various modules
 * and loading necessary data and configurations when the application is started.
 */
export default createStore({
  /**
   * Defines the various modules that make up the Vuex store for the application.
   * These modules include:
   * - `user`: manages user-related data and actions
   * - `platforms`: manages platform-related data and actions
   * - `config`: manages application configuration data and actions
   * - `includes`: manages the loading of various include files
   * - `validation`: manages the validation of form steps
   */
  modules: {
    user,
    platforms,
    config,
    includes,
    validation,
  },

  /**
   * Initializes the Vuex store for the application, including loading necessary data and configurations.
   * This action is responsible for:
   * - Initializing the application configuration
   * - Updating the visibility of the 'tpsp_internal_id' field based on the configuration
   * - Initializing the validation steps for the form
   * - Loading the platform data and local customizations
   * - Loading all required include files
   *
   * @param {Object} param0 - The Vuex action context object.
   * @param {Object} param0.dispatch - The Vuex dispatch function.
   * @param {Function} param0.rootGetters - The Vuex root getters function.
   * @param {Object} appProps - The application properties object, which is used to initialize the configuration.
   * @throws {Error} If the `appProps` object is invalid.
   */
  actions: {
    async initializeApp({ dispatch, rootGetters }, { appProps }) {
      if (appProps && typeof appProps === "object") {
        try {
          // Initialize config
          await dispatch("config/initializeConfig", appProps);

          // Update tpsp_internal_id visibility
          const showTpspInternalId = appProps.showTpspInternalId;
          await dispatch("user/updateFieldProperty", {
            field: "tpsp_internal_id",
            property: "showField",
            value: showTpspInternalId,
          });

          // Update tpsp_internal_id label if custom label provided
          if (appProps.tpspIdLabel) {
            await dispatch("user/updateFieldProperty", {
              field: "tpsp_internal_id",
              property: "label",
              value: appProps.tpspIdLabel,
            });
          }

          // After config is initialized, initialize steps validity
          const steps = rootGetters["config/getCurrentFormSteps"];
          await dispatch("validation/initializeSteps", steps);

          // Load platforms and local customisations and merge them
          await dispatch("platforms/initializePlatforms");
          // console.log("Platforms initialized");

          // Load all include files, try local fallback to /global version
          await dispatch("includes/loadAllIncludes", {
            fileNames: [
              "intro",
              "thanks",
              "platforms_intro_available",
              "platforms_intro_other",
              "additional_information_text",
              "platform_authorization_header",
              "finio_terms_and_conditions",
            ],
            tpspName: appProps.tpspName  // This will be 'Development Site'
          });

          console.log("Includes initialized");

          // URL parameter pre-filling
          // NOTE: Platform credentials (username/password) are intentionally not
          // pre-fillable via URL — they must always be entered manually on the form.
          const prefill = appProps.urlPrefill || { userFields: {}, platforms: [] };
          const hasAnyPrefill = Object.keys(prefill.userFields).length > 0 || prefill.platforms.length > 0;

          // Phase 1: Pre-fill user fields from URL params
          for (const [field, value] of Object.entries(prefill.userFields)) {
            await dispatch('user/updateUser', { field, value }, { root: true });
          }

          // Backward compat: ?tpspid= still works if ?tpsp_internal_id= wasn't provided
          if (!prefill.userFields.tpsp_internal_id && appProps.tpspInternalId) {
            await dispatch('user/updateUser', {
              field: 'tpsp_internal_id',
              value: appProps.tpspInternalId
            }, { root: true });
          }

          // Phase 2: Pre-select platforms
          const allPlatforms = rootGetters['platforms/getPlatformById'];
          for (const platformId of prefill.platforms) {
            const platform = allPlatforms(platformId);
            if (platform && platform.available !== false && !platform.hide) {
              await dispatch('platforms/updatePlatform', {
                id: platformId,
                changes: { selected: true }
              }, { root: true });
            }
          }

          // Set hasUrlPrefill flag for auto-advance
          if (hasAnyPrefill) {
            dispatch('config/initializeConfig', { hasUrlPrefill: true }, { root: true });
          }

          // Set visibility
          dispatch('user/updateTpspInternalIdVisibility',
            appProps.showTpspInternalId,
            { root: true }
          );

        } catch (error) {
          console.error("Error in initializeApp:", error);
          throw error;
        }
      } else {
        console.error("Invalid appProps:", appProps);
        throw new Error("Invalid appProps");
      }
    },
  },
});
